import { cn } from "@/lib/utils";
import {
  BriefcaseBusinessIcon,
  EllipsisIcon,
  MailIcon,
  RotateCcw,
  UserIcon,
  UserRoundX,
  UsersIcon,
} from "lucide-react";

import {
  Column,
  ColumnHeader,
  EmptyState,
  LoadingIndicator,
} from "@/components";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

import { useErrorToast } from "@/hooks/useErrorToast";
import { useSuccessToast } from "@/hooks/useSuccessToast";
import { useCancelUserInvitation } from "@/hooks/mutations/useCancelUserInvitation";
import { useResendUserInvitation } from "@/hooks/mutations/useResendUserInvitation";
import { useSetRemoveCompanyUserInCompanyUsersForSettings } from "@/hooks/sets/useSetRemoveCompanyUserInCompanyUsersForSettings";
import { CompanySummary, CompanyUser } from "@/types";

export const AccountSettingsCompanyUsersTable = ({
  company,
  companyUsers,
  setIsInviteUserDialogOpen,
}: {
  company: CompanySummary;
  companyUsers: CompanyUser[];
  setIsInviteUserDialogOpen: (isOpen: boolean) => void;
}) => {
  const {
    mutate: cancelUserInvitation,
    isPending: isCancelUserInvitationPending,
    variables: cancelUserInvitationVariables,
  } = useCancelUserInvitation();
  const {
    mutate: resendUserInvitation,
    isPending: isResendUserInvitationPending,
    variables: resendUserInvitationVariables,
  } = useResendUserInvitation();
  const setRemoveCompanyUserInCompanyUsersForSettings =
    useSetRemoveCompanyUserInCompanyUsersForSettings();
  const errorToast = useErrorToast();
  const successToast = useSuccessToast();

  // No error state, just a toast
  const isRowPending = (email: string) => {
    return (
      (isCancelUserInvitationPending &&
        cancelUserInvitationVariables.cancelUserInvitationRequest
          .inviteeEmail === email) ||
      (isResendUserInvitationPending &&
        resendUserInvitationVariables.resendUserInvitationRequest
          .inviteeEmail === email)
    );
  };

  const renderActionsCell = (companyUser: CompanyUser) => {
    const { email, isInvited } = companyUser;
    const isPending = isRowPending(email);

    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="relative"
            size="icon"
            variant="ghost"
            disabled={!isInvited || isPending}
          >
            <EllipsisIcon className={cn("h-4 w-4", isPending && "invisible")} />
            {isPending && (
              <LoadingIndicator
                className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2"
                variant="small"
              />
            )}
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() => {
              resendUserInvitation(
                {
                  resendUserInvitationRequest: {
                    inviteeEmail: email,
                  },
                },
                {
                  onSuccess: () => {
                    successToast({
                      description: "Invite resent successfully!",
                    });
                  },
                  onError: () => {
                    errorToast({
                      description: "Failed to resend invite",
                    });
                  },
                },
              );
            }}
          >
            <RotateCcw className="h-4 w-4" />
            Resend invite
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() => {
              cancelUserInvitation(
                {
                  cancelUserInvitationRequest: {
                    inviteeEmail: email,
                  },
                },
                {
                  onSuccess: () => {
                    setRemoveCompanyUserInCompanyUsersForSettings({
                      companyUserToRemove: companyUser,
                    });
                    successToast({
                      description: "User removed successfully!",
                    });
                  },
                  onError: () => {
                    errorToast({
                      description: "Failed to remove user",
                    });
                  },
                },
              );
            }}
          >
            <UserRoundX className="h-4 w-4" />
            Remove from workspace
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    );
  };

  const renderTableHeader = () => {
    return (
      <div className="rounded-t-lg border border-b-0 border-slate-200">
        <Table>
          <TableHeader
            className={cn(
              companyUsers.length === 0 && "pointer-events-none opacity-50",
            )}
          >
            <TableRow className="border-none">
              <TableHead className="w-[25%]">
                <ColumnHeader icon={UserIcon} label="Name" />
              </TableHead>
              <TableHead className="w-[33%]">
                <ColumnHeader icon={MailIcon} label="Email" />
              </TableHead>
              <TableHead className="w-[34%]">
                <ColumnHeader icon={BriefcaseBusinessIcon} label="Role" />
              </TableHead>
              <TableHead className="w-[8%]" />
            </TableRow>
          </TableHeader>
        </Table>
      </div>
    );
  };

  const renderTableBody = () => {
    if (companyUsers.length === 0) {
      return (
        <div className="rounded-b-lg border border-slate-200">
          <EmptyState
            className="bg-slate-50 py-14"
            description={`Invite users to join ${company.name}'s recruiter dashboard.`}
            handlePrimaryClick={() => {
              setIsInviteUserDialogOpen(true);
            }}
            Icon={UsersIcon}
            primaryButtonName="Add a user"
            primaryButtonVariant="outline"
            title={`Add a user to ${company.name}`}
          />
        </div>
      );
    }

    return (
      <div className="overflow-y-auto rounded-b-lg border border-slate-200">
        <Table>
          <TableBody>
            {companyUsers.map((companyUser) => {
              const { firstName, lastName, email, isInvited } = companyUser;
              return (
                <TableRow
                  className={cn(isInvited && "bg-slate-50")}
                  key={email}
                >
                  <TableCell className="w-[25%]">
                    <p
                      className={cn(
                        "font-medium",
                        isInvited ? "text-slate-500" : "text-slate-700",
                      )}
                    >
                      {firstName} {lastName}
                    </p>
                  </TableCell>
                  <TableCell className="w-[33%]">
                    <p
                      className={cn(
                        "font-medium",
                        isInvited ? "text-slate-500" : "text-slate-700",
                      )}
                    >
                      {email}
                    </p>
                  </TableCell>
                  <TableCell className="w-[34%]">
                    <p
                      className={cn(
                        "font-medium",
                        isInvited ? "text-slate-500" : "text-slate-700",
                      )}
                    >
                      {isInvited ? "Invited" : "User"}
                    </p>
                  </TableCell>
                  <TableCell className="w-[8%]">
                    {renderActionsCell(companyUser)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    );
  };

  return (
    <Column className="overflow-auto">
      {renderTableHeader()}
      {renderTableBody()}
    </Column>
  );
};
